import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { MetricCardComponent } from 'src/app/core/components/metric-card';
import { AverageMeanTimeToMitigateResponse } from 'src/app/core/models/analytics/vss-models';
import { formatDateRangeLabel } from 'src/app/core/shared/common/date-range.util';
import { environment } from 'src/environments/environment';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-vss-mttm-card',
  standalone: true,
  imports: [CommonModule, MetricCardComponent, DialogsModule, DatatableComponent],
  templateUrl: './vss-mttm-card.component.html',
  styleUrl: './vss-mttm-card.component.scss',
})
export class VssMttmCardComponent {
  private locale = inject(LOCALE_ID);

  averageMeanTimeToMitigateResponse = input<AverageMeanTimeToMitigateResponse>(new AverageMeanTimeToMitigateResponse());

  public organizationId = input<number | null>();
  public startDate = input<string | null>();
  public endDate = input<string | null>();

  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const start = this.startDate();
    const end = this.endDate();
    const label = start && end ? formatDateRangeLabel([start, end]) : '';
    return label ? `Average Mean Time To Mitigate (${label})` : 'Average Mean Time To Mitigate';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/vicarius/analytics/averagemeantimetomitigatedetails(${id})` : '';
  });

  /** The same period the card was computed for. */
  public periodParams = computed((): Record<string, string> => {
    const start = this.startDate();
    const end = this.endDate();
    return start && end
      ? { current_start: encodeURIComponent(start), current_end: encodeURIComponent(end) }
      : {};
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  public datatableColumns = [
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'cve_uid', name: 'cve_uid', title: 'CVE', type: 'string', render: text },
    { data: 'severity', name: 'severity', title: 'Severity', type: 'string', render: text },
    { data: 'detected_at', name: 'detected_at', title: 'Detected At', type: 'string', render: this.date },
    { data: 'mitigated_at', name: 'mitigated_at', title: 'Mitigated At', type: 'string', render: this.date },
    {
      data: 'mttm_days',
      name: 'mttm_days',
      title: 'MTTM',
      type: 'num',
      render: (value: number) => (value != null ? `${value.toFixed(2)} days` : ''),
    },
    { data: 'ingested_at', name: 'ingested_at', title: 'Ingested At', type: 'string', render: this.date },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
